import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft,
  Calculator,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';

const API = API_BASE;

const CreditEvaluationApp = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client');
  const { isManagerOrAdmin } = useAuth();
  
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [bankAnalyses, setBankAnalyses] = useState([]);
  const [tierRules, setTierRules] = useState([]);
  
  const [selectedClient, setSelectedClient] = useState(clientId || undefined);
  const [selectedAppraisal, setSelectedAppraisal] = useState(undefined);
  const [selectedBankAnalysis, setSelectedBankAnalysis] = useState(undefined);
  const [equipmentPrice, setEquipmentPrice] = useState(undefined);
  const [minDeposit, setMinDeposit] = useState(undefined);
  const [minBalance, setMinBalance] = useState(undefined);
  const [notes, setNotes] = useState(undefined);
  
  // Override state
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrideTier, setOverrideTier] = useState(undefined);
  const [overrideComment, setOverrideComment] = useState(undefined);

  useEffect(() => {
    fetchInitialData();
    if (id && id !== 'new') {
      fetchEvaluation();
    } else {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    // Auto-fill from selected appraisal
    if (selectedAppraisal) {
      const appraisal = appraisals.find(a => a.id === selectedAppraisal);
      if (appraisal?.asset?.requested_price) {
        setEquipmentPrice(appraisal.asset.requested_price.toString());
      }
    }
  }, [selectedAppraisal, appraisals]);

  useEffect(() => {
    // Auto-fill from selected bank analysis
    if (selectedBankAnalysis) {
      const analysis = bankAnalyses.find(a => a.id === selectedBankAnalysis);
      if (analysis) {
        // Get min_monthly_deposit from summary or direct field
        const minDep = analysis.summary?.min_monthly_deposit || analysis.min_monthly_deposit || 
                       analysis.summary?.min_deposit || 0;
        // Get min_ending_balance from summary or direct field
        const minBal = analysis.summary?.min_ending_balance || analysis.min_ending_balance || 
                       analysis.summary?.avg_ending_balance || 0;
        
        setMinDeposit(minDep.toString());
        setMinBalance(minBal.toString());
      }
    }
  }, [selectedBankAnalysis, bankAnalyses]);

  const fetchInitialData = async () => {
    try {
      const [clientsRes, appraisalsRes, bankRes, tiersRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/appraisals`),
        axios.get(`${API}/bank-analyses`),
        axios.get(`${API}/tier-rules`)
      ]);
      setClients(clientsRes.data);
      setAppraisals(appraisalsRes.data);
      setBankAnalyses(bankRes.data);
      setTierRules(tiersRes.data.tiers || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchEvaluation = async () => {
    try {
      const response = await axios.get(`${API}/credit-evaluations/${id}`);
      setEvaluation(response.data);
      setSelectedClient(response.data.client_id || undefined);
      setNotes(response.data.notes || '');
    } catch (error) {
      toast.error('Error al cargar la evaluación');
      navigate('/apps/credit-evaluation');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!equipmentPrice || !minDeposit || !minBalance) {
      toast.error('Precio del equipo, depósito mínimo y balance mínimo son requeridos');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/credit-evaluations`, {
        client_id: selectedClient || null,
        appraisal_id: selectedAppraisal || null,
        bank_analysis_id: selectedBankAnalysis || null,
        equipment_price: parseFloat(equipmentPrice),
        min_deposit: parseFloat(minDeposit),
        min_ending_balance: parseFloat(minBalance),
        notes
      });
      toast.success('Evaluación de crédito creada');
      navigate(`/apps/credit-evaluation/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear evaluación');
    } finally {
      setSaving(false);
    }
  };

  const handleOverride = async () => {
    if (!overrideTier || !overrideComment) {
      toast.error('Seleccione un tier e ingrese un comentario');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/credit-evaluations/${evaluation.id}/override`, {
        tier: parseInt(overrideTier),
        comment: overrideComment
      });
      toast.success('Tier actualizado');
      setOverrideMode(false);
      fetchEvaluation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al hacer override');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  // New evaluation form
  if (!id || id === 'new') {
    return (
      <div className="space-y-6" data-testid="new-credit-evaluation-page">
        <Button variant="ghost" onClick={() => navigate('/apps/credit-evaluation')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a Asignación de Riesgo
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-purple-600" />
              Nueva Asignación de Riesgo
            </CardTitle>
            <CardDescription>Asigna un tier basado en datos financieros</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Import from existing data */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Cliente (Opcional)</Label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin cliente</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Importar de Tasación</Label>
                <Select value={selectedAppraisal} onValueChange={setSelectedAppraisal}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguna</SelectItem>
                    {appraisals.filter(a => a.status === 'completed').map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.asset?.year} {a.asset?.make} - ${a.asset?.requested_price?.toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Importar de Análisis Bancario</Label>
                <Select value={selectedBankAnalysis} onValueChange={setSelectedBankAnalysis}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguno</SelectItem>
                    {bankAnalyses.filter(a => a.status === 'completed').map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.client_name || 'Sin cliente'} - ${a.min_monthly_deposit?.toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            {/* Manual input */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Precio del Equipo ($) *</Label>
                <Input
                  type="number"
                  value={equipmentPrice}
                  onChange={(e) => setEquipmentPrice(e.target.value)}
                  placeholder="85000"
                />
              </div>
              <div className="space-y-2">
                <Label>Depósito Mínimo Mensual ($) *</Label>
                <Input
                  type="number"
                  value={minDeposit}
                  onChange={(e) => setMinDeposit(e.target.value)}
                  placeholder="12000"
                />
              </div>
              <div className="space-y-2">
                <Label>Balance Final Mínimo ($) *</Label>
                <Input
                  type="number"
                  value={minBalance}
                  onChange={(e) => setMinBalance(e.target.value)}
                  placeholder="6000"
                />
              </div>
            </div>

            {/* Tier Preview */}
            {equipmentPrice && minDeposit && minBalance && (
              <div className="p-4 rounded-lg border bg-muted/50">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Vista Previa de Elegibilidad
                </h4>
                <div className="space-y-2">
                  {tierRules.map((tier) => {
                    const requiredDeposit = (tier.min_deposit_pct / 100) * parseFloat(equipmentPrice);
                    const requiredBalance = (tier.min_ending_balance_pct / 100) * parseFloat(equipmentPrice);
                    const qualifies = parseFloat(minDeposit) >= requiredDeposit && parseFloat(minBalance) >= requiredBalance;
                    
                    return (
                      <div key={tier.tier} className={cn(
                        'flex items-center justify-between p-2 rounded',
                        qualifies ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-muted'
                      )}>
                        <span className="font-medium">{tier.name}</span>
                        <div className="flex items-center gap-4 text-sm">
                          <span>Req. Dep: ${requiredDeposit.toLocaleString()}</span>
                          <span>Req. Bal: ${requiredBalance.toLocaleString()}</span>
                          {qualifies ? (
                            <CheckCircle className="w-4 h-4 text-emerald-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notas sobre la evaluación..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => navigate('/apps/credit-evaluation')}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={saving}>
                {saving ? 'Evaluando...' : 'Evaluar y Asignar Tier'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Existing evaluation view
  return (
    <div className="space-y-6" data-testid="credit-evaluation-detail-page">
      <Button variant="ghost" onClick={() => navigate('/apps/credit-evaluation')}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver a Evaluaciones
      </Button>

      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-purple-600" />
                Evaluación de Crédito
              </CardTitle>
              <CardDescription>
                {evaluation?.client_name ? `Cliente: ${evaluation.client_name}` : 'Sin cliente asociado'}
              </CardDescription>
            </div>
            {evaluation?.assigned_tier ? (
              <div className="flex flex-col items-end gap-1">
                <Badge className="text-lg py-1 px-3" variant="default">
                  Tier {evaluation.assigned_tier}
                </Badge>
                {evaluation.tier_override && (
                  <Badge className="bg-amber-500 text-white animate-pulse">
                    ⚠️ OVERRIDE POR SUPERVISOR
                  </Badge>
                )}
              </div>
            ) : (
              <Badge variant="destructive">Sin Tier</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 rounded-lg bg-muted">
              <p className="text-xs text-muted-foreground">Precio del Equipo</p>
              <p className="font-semibold">${evaluation?.equipment_price?.toLocaleString()}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted">
              <p className="text-xs text-muted-foreground">Depósito Mínimo</p>
              <p className="font-semibold">${evaluation?.min_deposit?.toLocaleString()}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted">
              <p className="text-xs text-muted-foreground">Balance Mínimo</p>
              <p className="font-semibold">${evaluation?.min_ending_balance?.toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tier Calculations */}
      <Card>
        <CardHeader>
          <CardTitle>Cálculos de Tier</CardTitle>
          <CardDescription>Elegibilidad basada en % del precio del equipo</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-2 text-left">Tier</th>
                  <th className="px-4 py-2 text-left">Req. Depósito</th>
                  <th className="px-4 py-2 text-left">Req. Balance</th>
                  <th className="px-4 py-2 text-left">Elegible</th>
                </tr>
              </thead>
              <tbody>
                {evaluation?.tier_calculations?.map((calc) => (
                  <tr key={calc.tier} className={cn(calc.qualifies && 'bg-emerald-50 dark:bg-emerald-900/20')}>
                    <td className="px-4 py-2 font-medium">{calc.name}</td>
                    <td className="px-4 py-2">
                      <span className="text-muted-foreground">{calc.min_deposit_pct}%</span>
                      <span className="block text-xs">${calc.required_deposit?.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-2">
                      <span className="text-muted-foreground">{calc.min_ending_balance_pct}%</span>
                      <span className="block text-xs">${calc.required_balance?.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-2">
                      {calc.qualifies ? (
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-muted-foreground" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Risk Flags */}
          {evaluation?.risk_flags?.length > 0 && (
            <div className="mt-4 space-y-2">
              {evaluation.risk_flags.map((flag, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm">{flag}</span>
                </div>
              ))}
            </div>
          )}

          {/* Override Button */}
          {isManagerOrAdmin() && !overrideMode && (
            <div className="mt-4">
              <Button variant="outline" onClick={() => setOverrideMode(true)}>
                Override Manual de Tier (Manager/Admin)
              </Button>
            </div>
          )}

          {/* Override Form */}
          {overrideMode && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-base">Override de Tier</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nuevo Tier</Label>
                    <Select value={overrideTier} onValueChange={setOverrideTier}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar tier..." />
                      </SelectTrigger>
                      <SelectContent>
                        {tierRules.map((t) => (
                          <SelectItem key={t.tier} value={t.tier.toString()}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Comentario (Requerido)</Label>
                    <Textarea
                      value={overrideComment}
                      onChange={(e) => setOverrideComment(e.target.value)}
                      placeholder="Justificación del override..."
                      rows={2}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setOverrideMode(false)}>Cancelar</Button>
                  <Button onClick={handleOverride} disabled={saving}>
                    {saving ? 'Guardando...' : 'Aplicar Override'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Override History Display */}
          {evaluation?.tier_override && (
            <Card className="mt-4 border-amber-400 bg-amber-50 dark:bg-amber-900/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-5 h-5" />
                  Tier Asignado por Override de Supervisor
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Tier Calculado Original</p>
                    <p className="font-medium">Tier {evaluation.calculated_tier || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tier Override Asignado</p>
                    <p className="font-bold text-lg text-amber-700">Tier {evaluation.assigned_tier}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-muted-foreground">Comentario del Supervisor</p>
                    <p className="font-medium">{evaluation.override_comment || 'Sin comentario'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-muted-foreground">Aplicado por</p>
                    <p className="font-medium">{evaluation.override_by || 'N/A'} - {evaluation.override_at ? new Date(evaluation.override_at).toLocaleString('es-ES') : 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CreditEvaluationApp;
